import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ClipboardCheck, RefreshCw } from 'lucide-react'
import { ADMIN_API_BASE_URL, Button, Skeleton } from '../../core/index.js'
import { CensusRequirementView, CensusTable, censusRange, describeRequirementFigure, todayDate } from '../../schedule/index.js'
import type { CensusKind } from '../../schedule/index.js'
import { getCensus, getCensusRequirements, recordCensus, type PartnerSession } from '../lib/partnerApi.js'
import { ErrorNote, INPUT_CLASS, Labeled, SuccessNote, describeError } from './formControls.js'

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

/**
 * Plays a partner's EHR writing a unit's census through its ADT feed (census
 * response plan F0): `PUT /facilities/{facilityId}/census` as the partner,
 * then the census as `GET .../census` answers it and what the day requires
 * under the facility's staffing plan. Needs `census:write`; the same record
 * the facility administrator writes by hand on the Dashboard's Today tab.
 */
export function CensusForm({ session, facilityId }: { session: PartnerSession; facilityId: string }) {
  const queryClient = useQueryClient()
  const today = todayDate()
  const [unitId, setUnitId] = useState('')
  const [date, setDate] = useState(today)
  const [census, setCensus] = useState('')
  const [kind, setKind] = useState<CensusKind | ''>('')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const censusKey = ['partner-simulator', 'census', session.clientId, facilityId]
  const censusQuery = useQuery({ queryKey: censusKey, queryFn: () => getCensus(ADMIN_API_BASE_URL ?? '', session, facilityId, censusRange(today)), enabled: Boolean(ADMIN_API_BASE_URL) })
  const requirementsKey = ['partner-simulator', 'census-requirements', session.clientId, facilityId, date]
  const requirementsQuery = useQuery({ queryKey: requirementsKey, queryFn: () => getCensusRequirements(ADMIN_API_BASE_URL ?? '', session, facilityId, { date }), enabled: Boolean(ADMIN_API_BASE_URL) && DATE_PATTERN.test(date) })
  const units = censusQuery.data?.units ?? []
  const chosenUnit = unitId !== '' ? unitId : units[0]?.id ?? ''
  const count = Number(census)
  const canSend = chosenUnit !== '' && DATE_PATTERN.test(date) && census.trim() !== '' && Number.isInteger(count) && count >= 0 && !isSending

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!canSend || !ADMIN_API_BASE_URL) {
      return
    }
    setIsSending(true)
    setError(null)
    setDone(null)
    try {
      const result = await recordCensus(ADMIN_API_BASE_URL, session, facilityId, [{ unitId: chosenUnit, date, census: count, ...(kind === '' ? {} : { kind }) }])
      const written = result.entries[0]
      setDone(`Recorded ${written?.census ?? count} on ${chosenUnit} for ${date} as ${written?.kind ?? 'the count'}.`)
      void queryClient.invalidateQueries({ queryKey: censusKey })
      void queryClient.invalidateQueries({ queryKey: ['partner-simulator', 'census-requirements', session.clientId, facilityId] })
    } catch (err) {
      setError(describeError(err, 'The census could not be recorded.'))
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="space-y-5">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-xs text-ink-mid">The EHR\'s ADT feed writes each unit\'s census - residents in beds, a count only - as the day is counted or as admissions and discharges are known ahead. A day\'s census is its PPD; the facility\'s staffing plan turns it into seats at its HPPD target. The facility administrator records the same thing by hand on the Tools tab.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Labeled label="Unit" htmlFor="census-unit" hint={units.length === 0 ? 'The facility lists no units yet; any unit id stands.' : undefined}>
            {units.length > 0 ? (
              <select id="census-unit" value={chosenUnit} onChange={(event) => setUnitId(event.target.value)} className={INPUT_CLASS}>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name} ({unit.id}){unit.beds !== null ? ` · ${unit.beds} beds` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <input id="census-unit" value={unitId} onChange={(event) => setUnitId(event.target.value)} className={INPUT_CLASS} placeholder="unit-a" />
            )}
          </Labeled>
          <Labeled label="Day" hint="In the facility\'s zone" htmlFor="census-date">
            <input id="census-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} className={INPUT_CLASS} />
          </Labeled>
          <Labeled label="Census" hint="Residents in beds on the unit that day" htmlFor="census-count">
            <input id="census-count" type="number" min={0} value={census} onChange={(event) => setCensus(event.target.value)} className={INPUT_CLASS} />
          </Labeled>
          <Labeled label="Kind" hint="Left to the API, a day up to today is actual and a later one expected" htmlFor="census-kind">
            <select id="census-kind" value={kind} onChange={(event) => setKind(event.target.value as CensusKind | '')} className={INPUT_CLASS}>
              <option value="">By the date</option>
              <option value="actual">Actual (counted)</option>
              <option value="expected">Expected</option>
            </select>
          </Labeled>
        </div>
        <Button type="submit" icon={ClipboardCheck} disabled={!canSend}>
          {isSending ? 'Sending…' : 'Record the census'}
        </Button>
        {error && <ErrorNote message={error} />}
        {done && <SuccessNote>{done}</SuccessNote>}
      </form>

      <div className="border-t border-marine/15 pt-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">What {date} requires</div>
          <Button icon={RefreshCw} variant="secondary" size="sm" disabled={censusQuery.isFetching} onClick={() => void Promise.all([censusQuery.refetch(), requirementsQuery.refetch()])}>
            Refresh
          </Button>
        </div>
        {requirementsQuery.isPending ? (
          <Skeleton />
        ) : requirementsQuery.isError ? (
          <ErrorNote message={describeError(requirementsQuery.error, 'What the day requires could not be read.')} onRetry={() => void requirementsQuery.refetch()} retrying={requirementsQuery.isFetching} />
        ) : (
          <ul className="divide-y divide-marine/10">
            {requirementsQuery.data.units.map(({ unit, figure, requirement }) => (
              <li key={unit.id} className="py-1.5">
                <div className="text-xs font-bold text-ink">
                  {unit.name} <span className="font-normal text-ink-mid">· {figure ? `census ${figure.census}, ${describeRequirementFigure(figure, date)}` : describeRequirementFigure(null, date)}</span>
                </div>
                {requirement && <CensusRequirementView requirement={requirement} />}
              </li>
            ))}
          </ul>
        )}
        <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">The census, two weeks back and four ahead</div>
        {censusQuery.isPending ? <Skeleton /> : censusQuery.isError ? <ErrorNote message={describeError(censusQuery.error, 'The census could not be read.')} onRetry={() => void censusQuery.refetch()} retrying={censusQuery.isFetching} /> : <CensusTable view={censusQuery.data} today={today} />}
      </div>
    </div>
  )
}
